import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, AlertCircle, RefreshCw, Plus, AlertTriangle } from 'lucide-react';
import { useOrders, type OrderListItem } from '../hooks/useOrders';
import { usePriorityList, usePriorityUpdate, calculatePriorityUpdate } from '../hooks';
import { useWebSocket, type WebSocketMessage } from '../hooks/useWebSocket';
import { OrderCreateModal } from '../components/OrderCreateModal';
import { Toast, type ToastType } from '../components/Toast';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import type { SalesOrderStatus } from 'scheduler-types';

const statusColors: Record<SalesOrderStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: SalesOrderStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// --- Sortable row for active (DnD) view ---

interface SortableOrderRowProps {
  order: OrderListItem;
  index: number;
  onNavigate: (id: string) => void;
}

function SortableOrderRow({ order, index, onNavigate }: SortableOrderRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: order.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onNavigate(order.id)}
      className={`
        flex items-center gap-4 px-4 py-3 bg-background border-b border-border cursor-pointer
        ${isDragging ? 'shadow-lg ring-2 ring-primary z-10 opacity-90' : ''}
        hover:bg-background-subtle transition-colors
      `}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-background-muted touch-none"
        aria-label={`Drag to reorder ${order.gpSalesOrderNumber}`}
      >
        <GripVertical className="h-5 w-5 text-foreground-muted" />
      </button>

      {/* Priority number */}
      <div className="w-8 text-center">
        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-xs font-medium text-primary">
          {index + 1}
        </span>
      </div>

      {/* Order number */}
      <div className="w-28">
        <span className="font-mono text-sm font-medium text-foreground">
          {order.gpSalesOrderNumber}
        </span>
      </div>

      {/* Customer */}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground truncate">
          {order.customerName}
        </div>
      </div>

      {/* Status */}
      <div className="hidden sm:block">
        <StatusBadge status={order.status} />
      </div>

      {/* Items */}
      <div className="hidden md:block text-sm text-foreground-muted w-20 text-right">
        {order.lineItemCount} item{order.lineItemCount !== 1 ? 's' : ''}
      </div>

      {/* Ship date */}
      <div className="hidden sm:block text-sm text-foreground-muted w-28">
        {order.expectedShipDate ? format(parseISO(order.expectedShipDate), 'MMM d, yyyy') : '-'}
      </div>

      {/* Updated */}
      <div className="hidden lg:block text-sm text-foreground-muted w-28">
        {order.updatedAt
          ? formatDistanceToNow(new Date(order.updatedAt), { addSuffix: true })
          : '-'}
      </div>
    </div>
  );
}

// --- Table row for non-active views ---

interface OrderRowProps {
  order: OrderListItem;
  onNavigate: (id: string) => void;
}

function OrderRow({ order, onNavigate }: OrderRowProps) {
  return (
    <tr
      onClick={() => onNavigate(order.id)}
      className="hover:bg-background-subtle transition-colors cursor-pointer"
    >
      <td className="px-4 py-4 whitespace-nowrap">
        <span className="font-mono text-sm font-medium text-foreground">
          {order.gpSalesOrderNumber}
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-foreground">
            {order.customerName}
          </div>
          <div className="text-xs text-foreground-muted">
            {order.customerId}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <StatusBadge status={order.status} />
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted">
        {order.lineItemCount} item{order.lineItemCount !== 1 ? 's' : ''}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm">
        {order.expectedShipDate ? (
          <div>
            <div className="text-foreground font-medium">
              {format(parseISO(order.expectedShipDate), 'MMM d, yyyy')}
            </div>
            {order.expectedShipDateWorst && order.expectedShipDateWorst !== order.expectedShipDate && (
              <div className="text-xs text-foreground-muted">
                worst: {format(parseISO(order.expectedShipDateWorst), 'MMM d')}
              </div>
            )}
          </div>
        ) : order.earliestPromisedShipDate ? (
          <div className="text-foreground-muted">
            promised {format(parseISO(order.earliestPromisedShipDate), 'MMM d')}
          </div>
        ) : (
          <span className="text-foreground-muted">-</span>
        )}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted">
        {order.updatedAt
          ? formatDistanceToNow(new Date(order.updatedAt), {
              addSuffix: true,
            })
          : '-'}
      </td>
    </tr>
  );
}

// --- Main page ---

export function OrdersPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SalesOrderStatus | undefined>(
    'active'
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const isActiveFilter = statusFilter === 'active';

  // Use priority list for active filter (sorted by priority), regular orders otherwise
  const priorityQuery = usePriorityList();
  const ordersQuery = useOrders({
    status: statusFilter,
    limit: 50,
  });

  // Pick the right data source
  const activeData = isActiveFilter ? priorityQuery : ordersQuery;
  const { isLoading, error, isFetching } = activeData;
  const refetch = activeData.refetch;

  // --- DnD state (only used when active filter) ---
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [staleDataWarning, setStaleDataWarning] = useState(false);
  const isDraggingRef = useRef(false);

  // WebSocket for stale data detection
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    if (message.type === 'schedule_updated') {
      if (isDraggingRef.current) {
        setStaleDataWarning(true);
      }
    }
  }, []);

  const channels = useMemo(() => ['schedule'], []);
  useWebSocket(channels, { onMessage: handleWebSocketMessage });

  const priorityMutation = usePriorityUpdate({
    onError: () => {
      setToast({ message: 'Failed to save priority change', type: 'error' });
    },
    onSuccess: () => {
      setStaleDataWarning(false);
    },
  });

  // Local state for optimistic reorder
  const [localOrders, setLocalOrders] = useState<OrderListItem[] | null>(null);

  const orders = useMemo(() => {
    if (isActiveFilter && localOrders) return localOrders;
    return (isActiveFilter ? priorityQuery.data?.data : ordersQuery.data?.data) ?? [];
  }, [isActiveFilter, localOrders, priorityQuery.data?.data, ordersQuery.data?.data]);

  // Reset local state when server data updates and mutation is idle
  useEffect(() => {
    if (isActiveFilter && priorityQuery.data?.data && !priorityMutation.isPending) {
      setLocalOrders(null);
    }
  }, [isActiveFilter, priorityQuery.data?.data, priorityMutation.isPending]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragStart() {
    isDraggingRef.current = true;
  }

  function handleDragEnd(event: DragEndEvent) {
    isDraggingRef.current = false;
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    if (staleDataWarning) {
      setToast({
        message: 'Data changed during drag. Please verify your change.',
        type: 'info',
      });
    }

    const oldIndex = orders.findIndex((o) => o.id === active.id);
    const newIndex = orders.findIndex((o) => o.id === over.id);
    const movedOrder = orders[oldIndex];

    if (oldIndex === -1 || newIndex === -1 || !movedOrder) return;

    const { afterOrderId, beforeOrderId } = calculatePriorityUpdate(
      orders,
      oldIndex,
      newIndex
    );

    // Optimistic update
    const newOrders = arrayMove(orders, oldIndex, newIndex);
    setLocalOrders(newOrders);

    priorityMutation.mutate({
      orderId: movedOrder.id,
      afterOrderId,
      beforeOrderId,
    });
  }

  function handleDragCancel() {
    isDraggingRef.current = false;
    setStaleDataWarning(false);
  }

  const handleNavigate = useCallback(
    (id: string) => navigate(`/orders/${id}`),
    [navigate]
  );

  // Pagination info
  const pagination = isActiveFilter
    ? priorityQuery.data?.pagination
    : ordersQuery.data?.pagination;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Orders</h1>
          <p className="text-sm text-foreground-muted mt-1">
            Manage sales orders and priorities
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isActiveFilter && priorityMutation.isPending && (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </div>
          )}
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Order
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-background-muted transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-foreground">Status:</label>
        <select
          value={statusFilter ?? 'all'}
          onChange={(e) =>
            setStatusFilter(
              e.target.value === 'all' ? undefined : (e.target.value as SalesOrderStatus)
            )
          }
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="complete">Complete</option>
          <option value="cancelled">Cancelled</option>
        </select>
        {pagination && (
          <span className="text-sm text-foreground-muted">
            Showing {orders.length} of {pagination.total} orders
          </span>
        )}
      </div>

      {/* Tip for active filter */}
      {isActiveFilter && orders.length > 0 && (
        <div className="bg-background-subtle rounded-lg px-4 py-3 text-sm text-foreground-muted">
          <strong className="font-medium text-foreground">Tip:</strong> Drag to reorder priorities. Higher position = higher priority.
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">
                Failed to load orders
              </h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 text-sm font-medium text-danger hover:underline"
          >
            Try again
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-12 w-12 rounded-full bg-background-muted flex items-center justify-center mb-4">
            <ClipboardIcon className="h-6 w-6 text-foreground-muted" />
          </div>
          <h3 className="text-lg font-medium text-foreground">No orders found</h3>
          <p className="text-sm text-foreground-muted mt-1">
            {statusFilter
              ? `No ${statusFilter} orders to display`
              : 'No orders to display'}
          </p>
        </div>
      ) : isActiveFilter ? (
        /* ---- Active filter: DnD sortable div-list ---- */
        <div className="bg-background rounded-lg border border-border overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-4 px-4 py-3 bg-background-subtle border-b border-border text-xs font-medium text-foreground-muted uppercase tracking-wider">
            <div className="w-6" /> {/* Drag handle spacer */}
            <div className="w-8 text-center">#</div>
            <div className="w-28">Order #</div>
            <div className="flex-1">Customer</div>
            <div className="hidden sm:block">Status</div>
            <div className="hidden md:block w-20 text-right">Items</div>
            <div className="hidden sm:block w-28">Ship Date</div>
            <div className="hidden lg:block w-28">Updated</div>
          </div>

          {/* Stale data warning */}
          {staleDataWarning && (
            <div className="flex items-center gap-2 px-4 py-2 bg-warning/10 border-b border-warning text-sm text-warning-foreground">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>Data has changed. Your drag may conflict with recent updates.</span>
              <button
                onClick={() => {
                  setStaleDataWarning(false);
                  refetch();
                }}
                className="ml-auto text-xs font-medium text-warning-foreground hover:underline"
              >
                Refresh
              </button>
            </div>
          )}

          {/* Sortable list */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={orders.map((o) => o.id)}
              strategy={verticalListSortingStrategy}
            >
              {orders.map((order, index) => (
                <SortableOrderRow
                  key={order.id}
                  order={order}
                  index={index}
                  onNavigate={handleNavigate}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        /* ---- Non-active filters: standard table ---- */
        <div className="bg-background rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background-subtle">
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Order #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Ship Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  onNavigate={handleNavigate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer info for active filter */}
      {isActiveFilter && orders.length > 0 && (
        <p className="text-sm text-foreground-muted text-center">
          {orders.length} active project{orders.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Create Order Modal */}
      <OrderCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {/* Toast notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          action={
            toast.type === 'update'
              ? {
                  label: 'Refresh',
                  onClick: () => {
                    refetch();
                    setToast(null);
                  },
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

// Simple clipboard icon for empty state
function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
      />
    </svg>
  );
}
